// @flow

import { AdvanceSearchTab, StandardSearchTab } from 'src/constants/search';

export const routes = {
  // Common
  oauth: {
    view: () => '/oauth'
  },
  betaFeatures: {
    view: () => '/beta-features'
  },
  // Landing page
  landingPage: {
    view: () => `/landing-page/new-features`
  },

  // onboarding new feature for old user before release day 042023
  onboardingOldUser: {
    view: () => `/onboarding-introduce-feature`
  },
  // Setup
  finalize: {
    view: () => '/oauth/finalize'
  },
  onboarding: {
    view: () => '/onboarding'
  },
  kyc: {
    view: () => '/kyc'
  },
  demographic: {
    view: () => '/demographic'
  },
  chooseLayout: {
    view: () => '/choose-layout'
  },
  initializing: {
    view: () => '/initializing'
  },
  setupComplete: {
    view: () => '/setup-complete'
  },
  verifySetup: {
    view: () => '/verify-setup'
  },
  newInstallation: {
    running: () => '/installation/running',
    onboarding: () => '/installation/onboarding',
    successInstall: () => '/installation/success-install',
    kyc: () => '/installation/kyc',
    chooseTheme: () => '/installation/choose-theme',
    survey: () => '/installation/survey',
    selectFeature: () => '/installation/select-feature',
    selectThemeNewSimplify: () => '/installation/select-theme-simplify',
    selectThemeNewSimplifyRunning: () => '/installation/select-theme-simplify-running',
    selectTemplate: () => '/installation/select-template-design',
    enableBoostCore: () => '/installation/enable-boost-core',
    complete: () => '/installation/completion',
    onboardingPlan: () => '/installation/onboarding-plan',
    onboardingPlanModalContent: () => '/installation/modal/onboarding-plan/content',
    setupTheme: themeId => `/installation/shopify-integration/${themeId}/new`,
    viewSetupTheme: themeId => `/installation/shopify-integration/${themeId}`
  },

  // Plan
  subscription: {
    plan: () => '/plan',
    addon: () => '/addon',
    addonModalContent: () => '/addon/modal/content',
    package: () => '/package',
    packageModalContent: () => '/package/modal/content',
    upgradePackage: () => '/upgrade-package',
    packageLarge: (planNumber: string) => `/plan-large/${planNumber}`,
    packageLargeModalContent: (planNumber: string) => `/plan-large/modal/${planNumber}`,
    discount: () => '/plan-discount',
    discountLarge: (planId: string) => `/plan-discount-large/${planId}`,
    planOffer: (planId: string) => `/plan-offer/${planId}`,
    complete: () => '/plan-complete',
    error: () => '/plan-error',
    migration: () => '/plan-migration',
    migrationComplete: () => '/plan-migration-complete',
    charge: () => '/charge',
    comparison: products => `/package-comparison/${products}`,
    comparisonModalContent: products => `/package-comparison/modal/${products}`,
    comparisonV5: () => `/package-comparison`,
    comparisonV5ModalContent: () => '/package-comparison-v5/modal/content',
    gmvBillingHistory: () => '/gmv-billing-history'
  },
  chargeCustomization: {
    view: () => '/charge-customization',
    complete: () => '/charge-customization-complete',
    error: () => '/charge-customization-error'
  },

  // Home
  dashboard: {
    view: () => '/',
    viewV3: () => '/dashboard-v3'
  },

  // Sync
  sync: {
    view: () => '/sync',
    settings: () => '/sync-settings'
  },

  // Filter
  filterTree: {
    new: () => '/filter-tree/new',
    view: (filterTreeId: string) => `/filter-tree/${filterTreeId}`,
    list: () => '/filter-tree'
  },
  filterSettings: {
    view: () => '/filter-settings'
  },
  filterLayout: {
    view: () => '/filter-layout'
  },

  // Theme
  themeSetup: {
    view: () => '/theme-setup'
  },
  supportedThemeSetupSuccess: {
    view: () => '/supported-theme-setup-success'
  },
  unsupportedThemeSetupSuccess: {
    view: () => '/unsupported-theme-setup-success'
  },
  themeRevert: {
    view: () => '/theme-revert'
  },
  themeRevertSuccess: {
    view: () => '/theme-revert-success'
  },
  switchLayout: {
    view: () => '/switch-layout'
  },
  switchLayoutSuccess: {
    view: () => '/switch-layout-success'
  },
  defaultLayoutSettings: {
    view: () => '/quick-view-cart-settings'
  },
  quickViewCartSettings: {
    view: () => '/quick-view-cart-settings'
  },
  defaultThemeSettings: {
    view: () => '/default-theme-settings'
  },
  themeUpgrade: {
    view: () => '/theme-upgrade',
    success: () => '/theme-upgrade-success',
    verify: () => '/verify-upgrade'
  },
  themeSetting: {
    visualEditor: (themeId: number) => `/theme-setting/${themeId}/visual-editor`,
    visualEditorModalContent: (themeId: number) => `/theme-setting/modal/visual-editor/${themeId}`,
    editVisualDesign: themeId => `/theme-setting/${themeId}/visual-editor`
  },
  // Theme Integration (SSR)
  shopifyIntegration: {
    view: () => '/shopify-integration',
    viewThemeWidget: themeId => `/shopify-integration/${themeId}`,
    newThemeWidget: themeId => `/shopify-integration/${themeId}/new`,
    previewBoostTemplate: themeNameLib =>
      `/shopify-integration/preview-boost-template/${themeNameLib}`,
    previewBoostTemplateModalContent: themeNameLib =>
      `/shopify-integration/modal/preview-boost-template/${themeNameLib}`,
    guide: (themeId: number, templateId: string, feature: string = '') =>
      `/shopify-integration/guide/${themeId}/${templateId}/${feature}`,
    guideModalContent: (themeId: number, templateId: string, feature: string = '') =>
      `/shopify-integration/modal/guide/${themeId}/${templateId}/${feature}`,

    codeEditor: (templateId: string) => `/shopify-integration/code-editor/${templateId}`,
    codeEditorModalContent: (templateId: string) =>
      `/shopify-integration/modal/code-editor/${templateId}`,
    manageTemplateInstance: (themeId: number) =>
      `/shopify-integration-template-instances/${themeId}`,
    templateLibrary: () => '/shopify-integration-template-library',
    viewShopifyIntegrationSimplify: themeId => `/shopify-integration-simplify/${themeId}`,
    shopifyIntegrationNewSimplify: themeId => `/shopify-integration-simplify/${themeId}/new`,
    upgradeToTurbo: (themeId: number) => `/shopify-integration/${themeId}/upgrade-to-turbo`
  },
  shopifyIntegrationSettings: {
    visualEditor: (templateId: string) =>
      `/shopify-integration-settings/visual-editor/${templateId}`,
    visualEditorModalContent: (templateId: string) =>
      `/shopify-integration-settings/modal/visual-editor/${templateId}`
  },

  // Tool
  translation: {
    view: () => '/translation',
    feature: (feature) => `/translation/${feature}`,
  },
  mergeValues: {
    view: () => '/merge-values',
    new: () => '/merge-values/new',
    edit: (type: string) => `/merge-values/${type}`
  },
  integration: {
    view: () => '/integration',
    thirdParty: {
      view: () => '/third-party'
    },
    klaviyo: {
      view: () => '/klaviyo-integration',
      install: () => '/klaviyo-integration/install',
      callback: () => '/klaviyo-integration/callback',
      callbackSuccess: () => '/klaviyo-integration/callback-success'
    },
    reviewsio: {
      view: () => '/reviewsio-integration'
    },
    attentive: {
      view: () => '/attentive-integration',
      callback: () => '/attentive-integration/callback',
      callbackSuccess: () => '/attentive-integration/callback-success'
    },
    globalE: {
      view: () => '/global-selling'
    },
    gorgias: {
      view: () => '/gorgias-integration',
    },
    shopifyCombinedListings: {
      view: () => '/shopify-combined-listings-integration',
    },
    'google-analytics-4': {
      view: () => '/google-analytics-4-integration',
      callback: () => '/google-analytics-4-integration/callback',
      callbackSuccess: () => '/google-analytics-4-integration/callback-success'
    }
  },
  productVisibility: {
    view: () => '/product-visibility'
  },
  metafield: {
    view: () => '/metafield'
  },
  customRangeSlider: {
    view: () => '/custom-range-slider'
  },
  swatchSettings: {
    view: () => '/swatch-settings'
  },
  customSorting: {
    view: () => '/custom-sorting'
  },
  variantsDisplay: {
    view: () => '/variants-display'
  },
  languageSetting: {
    view: () => '/language-setting'
  },
  account: {
    view: () => '/account'
  },
  appPreferences: {
    view: () => '/preferences'
  },
  generalSwatch: {
    view: () => '/general-swatch',
    settings: (filterName: string) => `/general-swatch/settings/${filterName}`
  },

  // Search unused
  instantSearchDisplay: {
    view: () => '/search/instant-search-display'
  },
  searchSettings: {
    view: () => '/search/search-settings'
  },
  searchPageDisplay: {
    view: () => '/search/search-page-display'
  },
  // end unused

  // Standard search
  stopWords: {
    view: () => `/search/standard-search/${StandardSearchTab.stopWords}`
  },
  redirect: {
    // view: () => `/search/standard-search/#${StandardSearchTab.redirects}`,
    // new: () => `/search/standard-search/#${StandardSearchTab.newRedirects}`,
    // edit: () => `/search/standard-search/#${StandardSearchTab.editRedirects}`
    view: () => `/search/standard-search/${StandardSearchTab.redirects}`,
    new: () => `/search/standard-search/${StandardSearchTab.newRedirects}`,
    edit: () => `/search/standard-search/${StandardSearchTab.editRedirects}`
  },
  suggestionDictionary: {
    view: () => `/search/standard-search/${StandardSearchTab.suggestionDictionary}`,
    new: () => `/search/standard-search/${StandardSearchTab.newSuggestionDictionary}`,
    edit: () => `/search/standard-search/${StandardSearchTab.editSuggestionDictionary}`
  },
  synonym: {
    view: () => `/search/standard-search/${StandardSearchTab.manualSynonyms}`,
    new: () => `/search/standard-search/${StandardSearchTab.newManualSynonyms}`,
    edit: () => `/search/standard-search/${StandardSearchTab.editManualSynonyms}`
  },
  instantSearchWidget: {
    view: () => `/search/standard-search/${StandardSearchTab.instantSearchWidget}`
  },
  searchControl: {
    view: () => `/search/search-control`
  },
  relevanceSettings: {
    view: () => `/search/standard-search/${StandardSearchTab.relevanceSettings}`
  },
  searchResultsPage: {
    view: () => `/search/standard-search/${StandardSearchTab.searchResultsPage}`
  },
  inCollectionSearch: {
    view: () => `/search/standard-search/${StandardSearchTab.inCollection}`
  },

  // Advanced search
  generalAdvancedSearch: {
    view: () => `/search/advanced-search/${AdvanceSearchTab.generalSearch}`
  },
  aiSynonym: {
    view: () => `/search/advanced-search/${AdvanceSearchTab.aiSynonyms}`
  },
  semanticSearch: {
    view: () => `/search/advanced-search/${AdvanceSearchTab.semanticSearch}`
  },
  testYourSearch: {
    view: () => `/search/test-your-search`,
    modalContent: () => `/search/test-your-search/modal/content`
  },
  productPerformanceRanking: {
    view: () => `/search/advanced-search/${AdvanceSearchTab.productPerformanceRanking}`
  },
  searchPersonalization: {
    view: () => `/search/advanced-search/${AdvanceSearchTab.searchPersonalization}`
  },
  // Personalization
  personalization: {
    view: () => `/personalization`
  },
  // Campaign planner
  campaignPlanner: {
    view: () => `/campaign-planner`
  },
  // Merchandising
  productRanking: {
    view: () => '/product-ranking'
  },
  merchandising: {
    view: () => '/merchandising',
    search: () => '/merchandising/search',
    collection: () => '/merchandising/collection',
    new: (ruleType: string) => `/merchandising/new/${ruleType}`,
    newModalContent: (ruleType: string) => `/merchandising/modal/new/${ruleType}`,
    edit: (ruleId: string, ruleType: string) => `/merchandising/${ruleType}/${ruleId}`,
    editModalContent: (ruleId: string) => `/merchandising/modal/${ruleId}`
  },
  analytics: {
    view: () => '/analytics',
    // For analytics v2
    // Search
    overviewSearch: () => '/search-analytics',
    searches: () => '/search-analytics/searches',
    searchTermDetail: (searchTerm: string) => `/search-analytics/searches/${searchTerm}`,
    searchesWithClick: () => '/search-analytics/searches-with-click',
    searchTermWithClickDetail: (searchTerm: string) =>
      `/search-analytics/searches-with-click/${searchTerm}`,
    searchesWithoutClicks: () => '/search-analytics/searches-without-clicks',
    searchesWithoutResults: () => '/search-analytics/searches-without-results',
    productPerformance: () => '/search-analytics/product-performance',

    //App impact
    appImpact: () => '/app-impact',

    // Collection
    overviewCollection: () => '/collection-analytics',
    collectionPerformance: () => '/collection-analytics/collection-performance',
    filterOptionValue: () => '/collection-analytics/filter-option-value',
    filterOptionValueCombination: () => '/collection-analytics/filter-option-value-combination',

    // Recommendation
    recommendationOverview: () => '/recommendation-analytics',
    recommendationPagePerformance: () => '/recommendation-analytics/page-performance',
    recommendationWidgetPerformance: () => '/recommendation-analytics/widget-performance',
    recommendationProductPerformance: () => '/recommendation-analytics/product-performance',

    // Pre-Order
    preOrderProduct: () => '/pre-order-analytics/products',
    preOrders: () => '/pre-order-analytics/pre-orders',

    // Settings
    settings: () => '/analytic-settings',

    // Post-purchase
    postPurchaseOffer: () => '/post-purchase-analytics/offer',
    postPurchasePage: () => '/post-purchase-analytics/page'
  },
  appPermissionSuccess: {
    view: () => '/update-permission-success',
    analyticsThemeV1: () => '/update-permission-success-theme-v1-analytics'
  },

  // Recommendation
  recommendationGeneralSettings: {
    settings: () => '/recommendation-settings'
  },
  recommendationWidgets: {
    list: (tab: number) => (tab ? `/recommendation-widgets?tab=${tab}` : '/recommendation-widgets'),
    new: () => '/recommendation-widgets/new',
    newModalContent: () => '/recommendation-widgets/modal/new',
    newPersonalized: () => '/recommendation-widgets/new-personalized',
    newPersonalizedModalContent: () => '/recommendation-widgets/modal/new-personalized',
    view: (widgetId: string) => `/recommendation-widgets/${widgetId}`,
    viewModalContent: (widgetId: string) => `/recommendation-widgets/modal/${widgetId}`,
    guide: () => '/recommendation-widgets-steps-guide',
    guideModalContent: () => '/recommendation-widgets/modal/steps-guide/content',
    guideCartDrawer: () => '/recommendation-widgets-guide-cart-drawer',
    guideCartDrawerModalContent: () => '/recommendation-widgets/modal/guide-cart-drawer/content'
  },
  manualRecommendation: {
    list: () => '/manual-recommendations',
    new: () => '/manual-recommendations/new',
    view: (productId: string) => `/manual-recommendations/${productId}`
  },
  predictiveBundle: {
    list: () => '/predictive-bundle',
    new: () => '/predictive-bundle/new',
    newModalContent: () => '/predictive-bundle/modal/new',
    view: (widgetId: string) => `/predictive-bundle/${widgetId}`,
    viewModalContent: (widgetId: string) => `/predictive-bundle/modal/${widgetId}`
  },
  recommendationDefaultDesignSettings: {
    view: () => '/recommendation-default-design-settings'
  },
  standardSearch: {
    view: hash => `/search/standard-search${hash ? `/#${hash}` : ''}`
  },
  advancedSearch: {
    view: hash => `/search/advanced-search${hash ? `/#${hash}` : ''}`
  },
  aiPowered: {
    view: () => `/ai-powered-landing`,
    modalContent: () => '/ai-powered-landing/modal/content'
  },
  landing: {
    turbo: () => `/turbo-landing`
  },
  settings: {
    view: () => '/settings'
  },
  globalSelling: {
    view: () => '/global-selling'
  },
  wholesaleSelling: {
    view: () => '/wholesale-selling'
  },
  backInStock: {
    settings: () => '/back-in-stock-settings',
    guide: () => '/back-in-stock-settings/guide',
    guideModalContent: () => '/back-in-stock-settings/modal/guide/content',
    subscription: () => '/back-in-stock-subscription',
    new: () => '/back-in-stock-subscription/new'
  },
  preOrder: {
    list: () => '/pre-order',
    new: () => '/pre-order/new',
    newModalContent: () => '/pre-order/modal/new',
    notifySetting: () => '/pre-order/notification-settings',
    view: (id: string) => `/pre-order/${id}`,
    viewModalContent: (id: string) => `/pre-order/modal/${id}`
  },
  tools: {
    domainSetting: () => '/tools/domain-settings'
  },
  abTesting: {
    list: () => '/ab-testing',
    new: type => `/ab-testing/${type}/new`,
    newModalContent: type => `/ab-testing/modal/${type}/new`,
    view: id => `/ab-testing/${id}/view`,
    viewModalContent: id => `/ab-testing/modal/${id}/view`,
    analytics: id => `/ab-testing/${id}/analytics`
  },
  countdown: {
    list: () => '/countdown',
    new: () => '/countdown/new',
    newModalContent: () => '/countdown/modal/new',
    view: id => `/countdown/${id}`,
    viewModalContent: id => `/countdown/modal/${id}`
  },
  postPurchase: {
    list: () => '/post-purchase',
    edit: id => `/post-purchase/${id}`,
    editModalContent: id => `/post-purchase/modal/${id}`,
    new: () => `/post-purchase/new`,
    newModalContent: () => `/post-purchase/modal/new`
  }
};
